// Card #3612 -- Message mode line breaks and a Small / Normal / Large size per
// line. CSS-free (like messageLengthTiers.js) so Cypress specs and the remote's
// editor can import it without dragging MessageLayout.css along.
//
// Stored shape (on the mode, no server validation: scoreboard modes pass
// through score-api as-is):
//   message      string   the lines' text joined with "\n". Still the field
//                         boardRenderState's blank check and every pre-#3612
//                         mode read, so it is ALWAYS written alongside
//   messageLines [{ text, size }]  size is "small" | "normal" | "large".
//                         Absent on every mode saved before #3612.
// A mode with no messageLines is a legacy plain message: ONE normal line
// holding the whole string, rendered by the same collapsing-whitespace div as
// before, so it looks exactly as it did.
import { MESSAGE_TEXT_MAX_LENGTH } from "score-common/constants";

export const MESSAGE_LINE_SIZE = { SMALL: "small", NORMAL: "normal", LARGE: "large" };
export const MESSAGE_LINE_SIZES = [MESSAGE_LINE_SIZE.SMALL, MESSAGE_LINE_SIZE.NORMAL, MESSAGE_LINE_SIZE.LARGE];
export const MESSAGE_LINE_MAX_COUNT = 10;

// Line font size relative to the tier's base size. MessageLayout.css carries
// the same numbers (one rule per size): messageLines.test.js pins them equal.
export const MESSAGE_LINE_SCALE = { small: 0.6, normal: 1, large: 1.5 };

// A forced line break leaves the rest of its line empty; on average about
// this many characters of the tier's width go unused per break.
export const MESSAGE_BREAK_COST_CHARS = 12;

export const isMessageLineSize = (size) => MESSAGE_LINE_SIZES.includes(size);

// The lines to render for a mode's stored fields. null when there is nothing
// to show (the caller's blank check has already routed that to Blank).
//
// messageLines is trusted only while its texts, joined with "\n", are exactly
// `message`. Anything else (an older client or a script that rewrote `message`
// and left a stale messageLines behind) falls back to the plain message, which
// is the field every reader and the blank check treat as the truth.
function trustedStoredLines(message, messageLines) {
  if (!Array.isArray(messageLines) || typeof message !== "string") return null;
  const lines = messageLines
    .filter((line) => line && typeof line.text === "string")
    .map((line) => ({ text: line.text, size: isMessageLineSize(line.size) ? line.size : MESSAGE_LINE_SIZE.NORMAL }));
  return lines.length > 0 && lines.map((line) => line.text).join("\n") === message ? lines : null;
}

export function resolveMessageLines(message, messageLines) {
  return trustedStoredLines(message, messageLines) ?? [{ text: typeof message === "string" ? message : "", size: MESSAGE_LINE_SIZE.NORMAL }];
}

// What the length tier is picked from: character count, weighted by how much
// board area a line's type takes (scale squared), plus the cost of each break.
// A legacy message (one normal line) is exactly its own length.
export function effectiveMessageLength(lines) {
  const chars = lines.reduce((sum, line) => sum + line.text.length * MESSAGE_LINE_SCALE[line.size] ** 2, 0);
  return chars + (lines.length - 1) * MESSAGE_BREAK_COST_CHARS;
}

// Rows of text the lines need at the very least, in Normal-line heights: each
// line takes one row of its own size however short it is (a break costs a whole
// row, not just the characters left unused). MessageLayout.css shrinks the base
// size when these rows would not fit the 702px text box.
export const messageLineUnits = (lines) => lines.reduce((sum, line) => sum + MESSAGE_LINE_SCALE[line.size], 0);

// Card #3619 -- the tier a MULTI-LINE or sized message uses is picked per canvas
// shape from a deterministic estimate of the rows its lines wrap to, not from
// the weighted character count above. The character count charges a Large line
// scale squared and a break a flat 12 characters, but a wrapped Large line
// costs whole rows (the box is only ~22 Large-em wide), so mixed-size messages
// near the 240 cap clipped (a real-Chrome fuzz: 30 of 120 renders). The
// estimate is pure arithmetic (no DOM measuring, so #1795's kiosk-fragility
// concern does not apply): chars -> em width (a conservative per-glyph table)
// x the line's font px, divided by the box width, rounded UP to whole rows,
// x the 1.25 line height. The largest tier whose total fits the 702px box wins;
// when even the floor tier does not fit, a per-shape squeeze (below 1) shrinks
// the lines until the estimate does. Plain messages (one Normal line within the
// cap) never come through here, they keep effectiveMessageLength's tier pick.
//
// The numbers below are MessageLayout.css's (the box is 702px tall, the text
// box 1689.6px wide at 16:9 and 2:1 and 1286.4px at 4:3, line-height 1.25, the
// 0.15em bottom cushion), and the tier font sizes per shape.
// messageLines.test.js pins the tier sizes and the box to the CSS.
export const MESSAGE_BOX_HEIGHT_PX = 702;
export const MESSAGE_LINE_HEIGHT = 1.25;
export const MESSAGE_CUSHION_EM = 0.15;
export const MESSAGE_FIT_SHAPES = [
  { shape: "16:9", varName: "--message-fit-16x9", suffix: null, boxWidth: 1689.6, tiers: [["short", 172.8], ["medium", 124.8], ["long", 76.8]] },
  { shape: "2:1", varName: "--message-fit-2x1", suffix: "s2x1", boxWidth: 1689.6, tiers: [["short", 172.8], ["medium", 124.8], ["long", 76.8], ["floor", 72]] },
  { shape: "4:3", varName: "--message-fit-4x3", suffix: "s4x3", boxWidth: 1286.4, tiers: [["short", 172.8], ["medium", 124.8], ["long", 76.8], ["floor", 60]] },
];

// Share of a row's width a wrapped line really fills: words break at spaces, so
// the end of each row is left short.
const WRAP_FILL = 0.86;

// Advance width in em of Montserrat Bold, the widest of the eight theme fonts
// (MessageLayout.css), rounded UP: lower case 0.66, upper case and digits 0.84,
// the wide capitals and the at sign 1.05, a space 0.3.
const WIDE_GLYPHS = "WMQ@%&";
export function messageGlyphEm(char) {
  if (char === " " || char === " ") return 0.3;
  if (WIDE_GLYPHS.includes(char)) return 1.05;
  if (char !== char.toLowerCase() || /[0-9]/.test(char)) return 0.84;
  if ("mw".includes(char)) return 0.98;
  return 0.66;
}

export function messageLineWidthEm(text) {
  let em = 0;
  for (const char of text) em += messageGlyphEm(char);
  return em;
}

// Height in px the lines take at tier font size `size`, scaled by `squeeze`.
// Mirrors MessageLayout.css: a line's font is its factor x min(tier size, the
// size at which --message-units rows fit the box) x the squeeze; the 0.15em
// cushion is the container's, so it does not shrink with the squeeze.
export function estimateMessageHeight(lines, size, squeeze, boxWidth) {
  const units = messageLineUnits(lines);
  const base = Math.min(size, (MESSAGE_BOX_HEIGHT_PX - MESSAGE_CUSHION_EM * size) / (units * MESSAGE_LINE_HEIGHT)) * squeeze;
  let height = MESSAGE_CUSHION_EM * size;
  for (const line of lines) {
    const font = MESSAGE_LINE_SCALE[line.size] * base;
    const rows = Math.max(1, Math.ceil((messageLineWidthEm(line.text) * font) / (boxWidth * WRAP_FILL)));
    height += rows * MESSAGE_LINE_HEIGHT * font;
  }
  return height;
}

// For each canvas shape: the largest tier the lines fit, or the floor tier and
// the squeeze that makes them fit.
export function messageFitPlan(lines) {
  return MESSAGE_FIT_SHAPES.map(({ shape, varName, suffix, boxWidth, tiers }) => {
    const fits = tiers.find(([, size]) => estimateMessageHeight(lines, size, 1, boxWidth) <= MESSAGE_BOX_HEIGHT_PX);
    if (fits) return { shape, varName, suffix, tier: fits[0], squeeze: 1 };
    const [floorTier, floorSize] = tiers[tiers.length - 1];
    let lo = 0.05;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (estimateMessageHeight(lines, floorSize, mid, boxWidth) <= MESSAGE_BOX_HEIGHT_PX) lo = mid;
      else hi = mid;
    }
    return { shape, varName, suffix, tier: floorTier, squeeze: Math.floor(lo * 1000) / 1000 };
  });
}

// Once the weighted length passes the cap even the floor tier is too big, so
// every line shrinks by this factor (1 = no squeeze, today's behavior).
export function messageSqueeze(effectiveLength, maxLength = MESSAGE_TEXT_MAX_LENGTH) {
  return effectiveLength > maxLength ? Math.sqrt(maxLength / effectiveLength) : 1;
}

// The editor's rows -> the two stored fields. Leading and trailing empty rows
// are dropped, interior empty rows stay (a deliberate gap). Returns nulls when
// nothing is left, which the save path stores as "no message".
export function messageFieldsFromLines(lines) {
  const rows = lines.map((line) => ({ text: line.text.trim(), size: isMessageLineSize(line.size) ? line.size : MESSAGE_LINE_SIZE.NORMAL }));
  while (rows.length > 0 && rows[0].text === "") rows.shift();
  while (rows.length > 0 && rows[rows.length - 1].text === "") rows.pop();
  if (rows.length === 0) return { message: null, messageLines: null };
  return { message: rows.map((row) => row.text).join("\n"), messageLines: rows };
}

// A stored mode -> the editor's rows (always at least one row).
export function editorLinesFromMode(mode) {
  const text = typeof mode?.message === "string" ? mode.message : "";
  const stored = trustedStoredLines(text, mode?.messageLines);
  if (stored) return stored;
  return text.split("\n").map((line) => ({ text: line, size: MESSAGE_LINE_SIZE.NORMAL }));
}

// Characters a row may still take: the cap covers the text plus the "\n"
// between rows, matching what is stored in `message`.
export function remainingLineChars(lines, index, maxLength = MESSAGE_TEXT_MAX_LENGTH) {
  const others = lines.reduce((sum, line, i) => (i === index ? sum : sum + line.text.length), 0);
  return Math.max(0, maxLength - others - (lines.length - 1));
}
